import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';

export const ns = 'handler-view-page';

export const useHandlerViewPageTranslation = (): UseTranslationResponse<typeof ns> => useTranslation(ns);

export const ruRu = (__LOCALE_RU__ ? ({
  handlerViewPage: {
    sections: {
      detection: 'Параметры детекции и оценки',
      match: 'Параметры сравнения',
      storage: 'Параметры сохранения',
      tags: 'Параметры добавления тегов',
      callbacks: 'Callbacks',
    },
    breadcrumbs: {
      back_to_handlers: 'Сценарии',
    },
    edit: 'Редактировать',
    owner_department: 'Подразделение владелец',
    load_error: 'Не удалось загрузить сценарий',
    back_to_list: 'Вернуться к сценариям',
    detection: {
      detect_faces: 'Обнаруживать лица',
      detect_bodies: 'Обнаруживать тела',
      face_attributes: 'Определяемые атрибуты лица',
      body_attributes: 'Определяемые атрибуты тела',
    },
    quality: {
      filter: 'Отбраковывать изображения лиц, не прошедших оценку качества',
      specularity_quality: 'Степень зеркальности изображения',
      image_modification: 'Модификация изображения',
    },
    match: {
      label: 'Название параметра сравнения',
      identify_among: 'Идентифицировать среди',
      search_by: 'Выполнять поиск по',
      list: 'Список',
      additional_search_params: 'Дополнительные параметры поиска',
      max_candidates: 'Максимальное количество похожих в результатах поиска',
      threshold: 'Пороговое значение точности',
    },
    filters: {
      all_conditions: 'При одновременном выполнении условий',
      additional_conditions: 'Дополнительные условия',
      gender: 'Пол',
      age: 'Возрастная категория',
      liveness: 'Liveness',
      deepfake: 'Deepfake',
      match_params: 'Параметры сравнения',
      similarity: 'С точностью',
    },
    storage: {
      face_sample: 'Биометрический образец лица',
      body_sample: 'Биометрический образец тела',
      image_origin: 'Исходное изображение',
      event: 'Событие',
      operator_panel: 'Получать и отображать событие в разделе «Панель оператора»',
      ttl: 'Время хранения',
    },
    tags: {
      tag: 'Название тега',
    },
    callbacks: {
      kafka: {
        type: 'Kafka',
        servers: 'Серверы',
        topic: 'Топик',
        protocol: 'Протокол',
        username: 'Имя пользователя',
        password: 'Пароль',
      },
      enable: 'Включен',
    },
  },
}) : {}) as Record<string, unknown>;

export const en = (__LOCALE_EN__ ? ({
  handlerViewPage: {
    sections: {
      detection: 'Detection and estimation parameters',
      match: 'Matching parameters',
      storage: 'Storage parameters',
      tags: 'Tagging parameters',
      callbacks: 'Callbacks',
    },
    breadcrumbs: {
      back_to_handlers: 'Policies',
    },
    edit: 'Edit',
    owner_department: 'Owner department',
    load_error: 'Failed to load the policy',
    back_to_list: 'Back to policies',
    detection: {
      detect_faces: 'Detect faces',
      detect_bodies: 'Detect bodies',
      face_attributes: 'Face attributes to estimate',
      body_attributes: 'Body attributes to estimate',
    },
    quality: {
      filter: 'Reject face images that failed the quality estimation',
      specularity_quality: 'Image specularity degree',
      image_modification: 'Image modification',
    },
    match: {
      label: 'Matching parameter name',
      identify_among: 'Identify among',
      search_by: 'Search by',
      list: 'List',
      additional_search_params: 'Additional search parameters',
      max_candidates: 'Maximum number of similar objects in search results',
      threshold: 'Similarity threshold',
    },
    filters: {
      all_conditions: 'When all conditions are met',
      additional_conditions: 'Additional conditions',
      gender: 'Gender',
      age: 'Age category',
      liveness: 'Liveness',
      deepfake: 'Deepfake',
      match_params: 'Matching parameters',
      similarity: 'With similarity',
    },
    storage: {
      face_sample: 'Face sample',
      body_sample: 'Body sample',
      image_origin: 'Original image',
      event: 'Event',
      operator_panel: 'Receive and display the event in the "Operator panel" section',
      ttl: 'Storage time',
    },
    tags: {
      tag: 'Tag name',
    },
    callbacks: {
      kafka: {
        type: 'Kafka',
        servers: 'Servers',
        topic: 'Topic',
        protocol: 'Protocol',
        username: 'Username',
        password: 'Password',
      },
      enable: 'Enabled',
    },
  },
}) : {}) as Record<string, unknown>;

export const esEs = (__LOCALE_ESES__ ? ({
  handlerViewPage: {
    sections: {
      detection: 'Parámetros de detección y estimación',
      match: 'Parámetros de comparación',
      storage: 'Parámetros de almacenamiento',
      tags: 'Parámetros de etiquetado',
      callbacks: 'Callbacks',
    },
    breadcrumbs: {
      back_to_handlers: 'Escenarios',
    },
    edit: 'Editar',
    owner_department: 'Departamento propietario',
    load_error: 'No se pudo cargar el escenario',
    back_to_list: 'Volver a escenarios',
    detection: {
      detect_faces: 'Detectar rostros',
      detect_bodies: 'Detectar cuerpos',
      face_attributes: 'Atributos faciales a determinar',
      body_attributes: 'Atributos corporales a determinar',
    },
    quality: {
      filter: 'Descartar imágenes de rostros que no superaron la evaluación de calidad',
      specularity_quality: 'Grado de especularidad de la imagen',
      image_modification: 'Modificación de la imagen',
    },
    match: {
      label: 'Nombre del parámetro de comparación',
      identify_among: 'Identificar entre',
      search_by: 'Buscar por',
      list: 'Lista',
      additional_search_params: 'Parámetros de búsqueda adicionales',
      max_candidates: 'Número máximo de similares en los resultados de búsqueda',
      threshold: 'Valor umbral de precisión',
    },
    filters: {
      all_conditions: 'Cuando se cumplan todas las condiciones',
      additional_conditions: 'Condiciones adicionales',
      gender: 'Género',
      age: 'Categoría de edad',
      liveness: 'Liveness',
      deepfake: 'Deepfake',
      match_params: 'Parámetros de comparación',
      similarity: 'Con precisión',
    },
    storage: {
      face_sample: 'Muestra biométrica facial',
      body_sample: 'Muestra biométrica corporal',
      image_origin: 'Imagen original',
      event: 'Evento',
      operator_panel: 'Recibir y mostrar el evento en la sección «Panel del operador»',
      ttl: 'Tiempo de almacenamiento',
    },
    tags: {
      tag: 'Nombre de la etiqueta',
    },
    callbacks: {
      kafka: {
        type: 'Kafka',
        servers: 'Servidores',
        topic: 'Tema',
        protocol: 'Protocolo',
        username: 'Usuario',
        password: 'Contraseña',
      },
      enable: 'Habilitado',
    },
  },
}) : {}) as Record<string, unknown>;

export const ptBr = (__LOCALE_PTBR__ ? ({
  handlerViewPage: {
    sections: {
      detection: 'Parâmetros de detecção e estimativa',
      match: 'Parâmetros de comparação',
      storage: 'Parâmetros de armazenamento',
      tags: 'Parâmetros de etiquetagem',
      callbacks: 'Callbacks',
    },
    breadcrumbs: {
      back_to_handlers: 'Cenários',
    },
    edit: 'Editar',
    owner_department: 'Departamento proprietário',
    load_error: 'Falha ao carregar o cenário',
    back_to_list: 'Voltar aos cenários',
    detection: {
      detect_faces: 'Detectar rostos',
      detect_bodies: 'Detectar corpos',
      face_attributes: 'Atributos faciais a determinar',
      body_attributes: 'Atributos corporais a determinar',
    },
    quality: {
      filter: 'Descartar imagens de rostos que não passaram na avaliação de qualidade',
      specularity_quality: 'Grau de especularidade da imagem',
      image_modification: 'Modificação da imagem',
    },
    match: {
      label: 'Nome do parâmetro de comparação',
      identify_among: 'Identificar entre',
      search_by: 'Buscar por',
      list: 'Lista',
      additional_search_params: 'Parâmetros de busca adicionais',
      max_candidates: 'Número máximo de similares nos resultados da busca',
      threshold: 'Valor limite de precisão',
    },
    filters: {
      all_conditions: 'Quando todas as condições forem atendidas',
      additional_conditions: 'Condições adicionais',
      gender: 'Gênero',
      age: 'Categoria de idade',
      liveness: 'Liveness',
      deepfake: 'Deepfake',
      match_params: 'Parâmetros de comparação',
      similarity: 'Com precisão',
    },
    storage: {
      face_sample: 'Amostra biométrica facial',
      body_sample: 'Amostra biométrica corporal',
      image_origin: 'Imagem original',
      event: 'Evento',
      operator_panel: 'Receber e exibir o evento na seção «Painel do operador»',
      ttl: 'Tempo de armazenamento',
    },
    tags: {
      tag: 'Nome da etiqueta',
    },
    callbacks: {
      kafka: {
        type: 'Kafka',
        servers: 'Servidores',
        topic: 'Tópico',
        protocol: 'Protocolo',
        username: 'Usuário',
        password: 'Senha',
      },
      enable: 'Ativado',
    },
  },
}) : {}) as Record<string, unknown>;
